import React, { useState, useEffect, useRef, useCallback } from "react";
import { processService } from "../../services/process.service";
import { clicksignService } from "../../services/clicksign.service";
import { useActiveProvider } from "../../hooks/useActiveProvider";
import { useProcess } from "../../contexts/ProcessContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Plus, Trash, Edit, FileText, AlertTriangle, Upload, Loader2, CornerDownRight, Download, ChevronDown } from "lucide-react";
import { documentTypeService } from "../../services/document_type.service";

const { addProcessoDocumento, updateProcessoDocumento, removeProcessoDocumento } = processService;

const EMPTY_DOC = {
  tipo: "", template_token: "", clicksign_template_id: null,
  parent_id: null, etapa: 1, obrigatoria: true,
  send_automatic_email: true, send_automatic_whatsapp: false,
  reenviar_se_banco_mudar: false
};

const DEFAULT_TIPO_COLORS = {
  termo:      "bg-statusInfoBg text-statusInfo border-statusInfo/20",
  cessao:     "bg-violet-50 text-violet-700 border-violet-200",
  anuencia:   "bg-purple-50 text-purple-700 border-purple-200",
  honorarios: "bg-statusWarningBg text-statusWarning border-statusWarning/20",
  outro:      "bg-muted text-muted-foreground border-border",
};

function tipoColor(slug) {
  return DEFAULT_TIPO_COLORS[slug] ?? "bg-muted text-muted-foreground border-border";
}

const EtapaBadge = ({ etapa, obrigatoria }) => (
  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ${
    obrigatoria
      ? "bg-sky-50 text-sky-600 border-sky-200"
      : "bg-purple-50 text-purple-600 border-purple-200"
  }`}>
    {`Etapa ${etapa} · ${obrigatoria ? "Obrigatória" : "Complementar"}`}
  </span>
);

export function DocumentsTab() {
  const { processo, refresh: onRefresh } = useProcess();
  const [activeTab, setActiveTab] = useState("documentos");
  const [isDocDialogOpen, setIsDocDialogOpen] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [newDocData, setNewDocData] = useState(EMPTY_DOC);

  const { activeProvider } = useActiveProvider();
  const [processoTemplates, setProcessoTemplates] = useState([]);

  const [docTypes, setDocTypes] = useState([]);

  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNome, setUploadNome] = useState("");
  const [uploadTipo, setUploadTipo] = useState("custom");
  const [uploading, setUploading] = useState(false);
  const [uploadTagMap, setUploadTagMap] = useState(null);
  const fileInputRef = useRef(null);

  const loadProcessoTemplates = useCallback(() => {
    if (!processo?.id) return Promise.resolve();
    return clicksignService.getProcessoTemplates(processo.id)
      .then(setProcessoTemplates)
      .catch(err => { toast.error(err?.response?.data?.detail || "Erro ao carregar templates."); });
  }, [processo?.id]);

  useEffect(() => {
    async function load() {
      await Promise.all([
        loadProcessoTemplates(),
        documentTypeService.listAll()
          .then(types => setDocTypes(types.filter(t => t.is_active)))
          .catch(e => toast.error(e?.response?.data?.detail || "Erro ao carregar tipos de documento.")),
      ]);
    }
    load();
  }, [processo?.id, loadProcessoTemplates]);

  const tipoLabels = Object.fromEntries(docTypes.map(t => [t.slug, t.label]));
  const tipoLabel = (slug) => tipoLabels[slug] ?? slug;

  const isClicksign = activeProvider?.name === "clicksign";
  const temValores = processo?.tem_valores ?? true;
  const temCessao = processo?.tem_cessao ?? true;

  const selectedTemplate = isClicksign && newDocData.clicksign_template_id
    ? processoTemplates.find(t => t.id === newDocData.clicksign_template_id)
    : null;

  const showValueTagWarning = !temValores && (selectedTemplate?.tag_map
    ? Object.values(selectedTemplate.tag_map).some(v =>
        v.field?.startsWith("deal.valor") || v.field === "deal.calculado"
      )
    : false);

  const resetForm = () => { setEditingDocId(null); setNewDocData(EMPTY_DOC); };

  const handleSaveDocument = async () => {
    if (!processo) return;
    const payload = { ...newDocData };
    if (isClicksign) payload.template_token = null;
    else payload.clicksign_template_id = null;
    try {
      if (editingDocId) {
        await updateProcessoDocumento(processo.id, editingDocId, payload);
        toast.success("Documento atualizado");
      } else {
        await addProcessoDocumento(processo.id, payload);
        toast.success("Documento adicionado");
      }
      setIsDocDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (e) {
      toast.error("Erro ao salvar documento: " + e.message);
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDocId(doc.id);
    setNewDocData({
      tipo: doc.tipo,
      template_token: doc.template_token ?? "",
      clicksign_template_id: doc.clicksign_template_id ?? null,
      parent_id: doc.parent_id,
      etapa: doc.etapa ?? 1,
      obrigatoria: doc.obrigatoria ?? true,
      send_automatic_email: doc.send_automatic_email ?? true,
      send_automatic_whatsapp: doc.send_automatic_whatsapp ?? false,
      reenviar_se_banco_mudar: doc.reenviar_se_banco_mudar ?? false,
    });
    setIsDocDialogOpen(true);
  };

  const handleRemoveDocument = async (docId) => {
    if (!processo) return;
    try {
      await removeProcessoDocumento(processo.id, docId);
      toast.success("Documento removido");
      onRefresh();
    } catch (e) {
      toast.error("Erro ao remover documento");
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadFile || !uploadNome.trim()) return;
    setUploading(true);
    try {
      const result = await clicksignService.uploadProcessoTemplate(processo.id, uploadFile, uploadNome.trim(), uploadTipo);
      setUploadTagMap(result?.tag_map ?? {});
      setUploadFile(null);
      setUploadNome("");
      setUploadTipo("custom");
      loadProcessoTemplates();
    } catch {
      toast.error("Erro ao enviar template");
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadTagMap(null);
    setUploadFile(null);
    setUploadNome("");
    setUploadTipo("custom");
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Remover este template? O arquivo será excluído do S3.")) return;
    try {
      await clicksignService.deleteProcessoTemplate(processo.id, templateId);
      toast.success("Template removido");
      loadProcessoTemplates();
    } catch {
      toast.error("Erro ao remover template");
    }
  };

  const templateName = (doc) => {
    if (doc.clicksign_template_id) {
      const t = processoTemplates.find(t => t.id === doc.clicksign_template_id);
      return t?.nome ?? doc.clicksign_template_id.slice(0, 8) + "…";
    }
    return doc.template_token ?? null;
  };

  const rootDocs = processo.documentos?.filter(d => !d.parent_id) ?? [];
  const docsCount = processo.documentos?.length ?? 0;

  return (
    <Card className="p-0 overflow-hidden">
      {/* Tab bar */}
      <div className="flex items-center border-b border-border px-4 pt-4 gap-1">
        <TabButton
          active={activeTab === "documentos"}
          onClick={() => setActiveTab("documentos")}
          label="Documentos"
          count={docsCount}
        />
        {isClicksign && (
          <TabButton
            active={activeTab === "templates"}
            onClick={() => setActiveTab("templates")}
            label="Templates DOCX"
            count={processoTemplates.length}
          />
        )}

        {/* Actions pushed to the right */}
        <div className="ml-auto flex gap-2 pb-3">
          {activeTab === "templates" && isClicksign && (
            <Button size="sm" variant="outline" onClick={() => {
              setUploadFile(null); setUploadNome(""); setUploadTipo("custom");
              setUploadDialogOpen(true);
            }}>
              <Upload className="mr-1.5 h-3.5 w-3.5" /> Enviar Template
            </Button>
          )}
          {activeTab === "documentos" && (
            <Dialog open={isDocDialogOpen} onOpenChange={(open) => { setIsDocDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm"><Plus className="mr-1.5 h-3.5 w-3.5" /> Adicionar</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingDocId ? "Editar Documento" : "Adicionar Documento"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={newDocData.tipo} onValueChange={(val) => setNewDocData({ ...newDocData, tipo: val })}>
                      <SelectTrigger><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                      <SelectContent>
                        {docTypes
                          .filter(t => temCessao || !["cessao", "anuencia"].includes(t.slug))
                          .map(t => (
                            <SelectItem key={t.slug} value={t.slug}>{t.label}</SelectItem>
                          ))}
                        {docTypes.length === 0 && <SelectItem value="" disabled>Carregando...</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>

                  {isClicksign ? (
                    <div className="space-y-2">
                      <Label>Template ClickSign</Label>
                      <Select
                        value={newDocData.clicksign_template_id ?? ""}
                        onValueChange={(val) => setNewDocData({ ...newDocData, clicksign_template_id: val || null })}
                      >
                        <SelectTrigger><SelectValue placeholder="Selecione um template" /></SelectTrigger>
                        <SelectContent>
                          {processoTemplates.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {showValueTagWarning && (
                        <div className="flex items-start gap-2 px-3 py-2.5 bg-statusWarningBg border border-statusWarning/20 rounded-lg text-xs text-statusWarning">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
                          <span>
                            Este template contém tags de valor financeiro, mas o processo está configurado como{" "}
                            <strong>sem valores</strong>. Os campos ficarão em branco.
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Token do Modelo (ZapSign)</Label>
                      <Input
                        value={newDocData.template_token}
                        onChange={e => setNewDocData({ ...newDocData, template_token: e.target.value })}
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Etapa</Label>
                    <Input
                      type="number"
                      min={1}
                      value={newDocData.etapa ?? 1}
                      onChange={e => setNewDocData({ ...newDocData, etapa: parseInt(e.target.value) || 1 })}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="doc_obrigatoria"
                      checked={newDocData.obrigatoria ?? true}
                      onCheckedChange={(checked) => setNewDocData({ ...newDocData, obrigatoria: checked })}
                    />
                    <Label htmlFor="doc_obrigatoria" className="font-normal">Aceite obrigatório</Label>
                  </div>

                  <div className="space-y-2">
                    <Label>Documento Principal (Opcional)</Label>
                    <Select
                      value={newDocData.parent_id ? String(newDocData.parent_id) : "none"}
                      onValueChange={(val) => setNewDocData({ ...newDocData, parent_id: val === "none" ? null : parseInt(val) })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum (documento principal)</SelectItem>
                        {processo.documentos?.filter(d => !d.parent_id && d.id !== editingDocId).map(doc => (
                          <SelectItem key={doc.id} value={String(doc.id)}>
                            {tipoLabel(doc.tipo)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-3 pt-1">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="doc_send_email"
                        checked={newDocData.send_automatic_email}
                        onCheckedChange={(checked) => setNewDocData({ ...newDocData, send_automatic_email: checked })}
                      />
                      <Label htmlFor="doc_send_email" className="font-normal">Enviar e-mail automático</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="doc_send_whatsapp"
                        checked={newDocData.send_automatic_whatsapp}
                        onCheckedChange={(checked) => setNewDocData({ ...newDocData, send_automatic_whatsapp: checked })}
                      />
                      <Label htmlFor="doc_send_whatsapp" className="font-normal">Enviar WhatsApp automático</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="doc_reenviar_banco"
                        checked={newDocData.reenviar_se_banco_mudar ?? false}
                        onCheckedChange={(checked) => setNewDocData({ ...newDocData, reenviar_se_banco_mudar: checked })}
                      />
                      <Label htmlFor="doc_reenviar_banco" className="font-normal">Reenviar se banco mudar</Label>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleSaveDocument}>Salvar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Tab: Documentos */}
      {activeTab === "documentos" && (
        <div className="p-5">
          {docsCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground border border-dashed border-border rounded-xl">
              <FileText className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-sm">Nenhum documento configurado</p>
              <p className="text-xs mt-1">Use "Adicionar" para vincular um template ao processo</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rootDocs.map(doc => {
                const children = processo.documentos?.filter(c => c.parent_id === doc.id) ?? [];
                const tName = templateName(doc);
                return (
                  <div key={doc.id} className="rounded-xl border border-border overflow-hidden">
                    <DocRow
                      doc={doc}
                      tName={tName}
                      tipoLabel={tipoLabel}
                      onEdit={() => handleEditDocument(doc)}
                      onRemove={() => handleRemoveDocument(doc.id)}
                    />
                    {children.map(child => (
                      <ChildDocRow
                        key={child.id}
                        doc={child}
                        tName={templateName(child)}
                        tipoLabel={tipoLabel}
                        onEdit={() => handleEditDocument(child)}
                        onRemove={() => handleRemoveDocument(child.id)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab: Templates DOCX */}
      {activeTab === "templates" && isClicksign && (
        <div className="p-5">
          {processoTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground border border-dashed border-border rounded-xl">
              <Upload className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-sm">Nenhum template enviado</p>
              <p className="text-xs mt-1">Use "Enviar Template" para adicionar um arquivo .docx</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden divide-y divide-slate-100">
              {processoTemplates.map(t => {
                const usedBy = processo.documentos?.filter(d => d.clicksign_template_id === t.id) ?? [];
                const tagEntries = t.tag_map ? Object.entries(t.tag_map) : [];
                const isExpanded = expandedTemplateId === t.id;
                return (
                  <div key={t.id} className="bg-card">
                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors">
                      <FileText className="w-4 h-4 text-slate-300 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{t.nome}</p>
                        {usedBy.length > 0 ? (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Usado em: {usedBy.map(d => tipoLabel(d.tipo)).join(", ")}
                          </p>
                        ) : (
                          <p className="text-xs text-amber-500 mt-0.5">Não vinculado a nenhum documento</p>
                        )}
                      </div>
                      {tagEntries.length > 0 && (
                        <button
                          onClick={() => setExpandedTemplateId(isExpanded ? null : t.id)}
                          className="flex items-center gap-1 text-xs text-blue-600 bg-statusInfoBg hover:bg-statusInfoBg px-2 py-0.5 rounded-full transition-colors shrink-0"
                          title="Ver tags do template"
                        >
                          <span>{tagEntries.length} tag{tagEntries.length !== 1 ? "s" : ""}</span>
                          <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                      )}
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${tipoColor(t.tipo)}`}>
                        {tipoLabel(t.tipo)}
                      </span>
                      {t.s3_url && (
                        <a
                          href={t.s3_url}
                          download={`${t.nome}.docx`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-md text-slate-300 hover:text-blue-500 hover:bg-statusInfoBg transition-colors shrink-0"
                          title="Baixar arquivo .docx"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <button
                        onClick={() => handleDeleteTemplate(t.id)}
                        className="p-1.5 rounded-md text-slate-300 hover:text-red-500 hover:bg-statusErrorBg transition-colors shrink-0"
                        title="Remover template"
                      >
                        <Trash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {isExpanded && tagEntries.length > 0 && (
                      <div className="border-t border-border px-4 py-3 bg-muted">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Tags encontradas</p>
                        <div className="flex flex-wrap gap-1.5">
                          {tagEntries.map(([tag, info]) => (
                            <span
                              key={tag}
                              title={info?.field || "tag desconhecida"}
                              className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-full border ${
                                info?.known
                                  ? "bg-statusSuccessBg border-statusSuccess/20 text-statusSuccess"
                                  : "bg-statusWarningBg border-statusWarning/20 text-statusWarning"
                              }`}
                            >
                              {tag}
                              {info?.field && <span className="text-[10px] opacity-70 font-sans not-italic">→ {info.field}</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Upload Template Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={handleCloseUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {uploadTagMap !== null ? "Template enviado" : "Enviar Template .docx"}
            </DialogTitle>
          </DialogHeader>

          {uploadTagMap !== null ? (
            /* ── Feedback pós-upload ── */
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 text-statusSuccess bg-statusSuccessBg border border-statusSuccess/20 rounded-lg px-3 py-2.5">
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                <span className="text-sm font-medium">Template salvo com sucesso.</span>
              </div>

              {Object.keys(uploadTagMap).length === 0 ? (
                <div className="text-sm text-muted-foreground bg-muted border border-border rounded-lg px-3 py-3">
                  Nenhuma tag <code className="text-xs bg-muted px-1 rounded">{"{{TAG}}"}</code> encontrada no documento.
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {Object.keys(uploadTagMap).length} tag{Object.keys(uploadTagMap).length !== 1 ? "s" : ""} encontrada{Object.keys(uploadTagMap).length !== 1 ? "s" : ""}:
                  </p>
                  <div className="rounded-lg border border-border overflow-hidden divide-y divide-slate-100 max-h-64 overflow-y-auto">
                    {Object.entries(uploadTagMap).map(([tag, info]) => (
                      <div key={tag} className="flex items-center justify-between gap-3 px-3 py-2 bg-card hover:bg-muted">
                        <code className="text-xs font-mono font-semibold text-statusInfo bg-statusInfoBg px-1.5 py-0.5 rounded shrink-0">
                          {tag}
                        </code>
                        <span className={`text-xs text-right truncate ${info?.known ? "text-emerald-600" : "text-amber-500"}`}>
                          {info?.field || <span className="italic text-muted-foreground">tag desconhecida</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Estas tags serão substituídas automaticamente ao gerar os documentos do cedente.
                  </p>
                </div>
              )}

              <DialogFooter>
                <Button onClick={handleCloseUploadDialog}>Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            /* ── Formulário de upload ── */
            <>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Nome do Template</Label>
                  <Input
                    value={uploadNome}
                    onChange={e => setUploadNome(e.target.value)}
                    placeholder="Ex: Contrato de Cessão v2"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <select
                    value={uploadTipo}
                    onChange={e => setUploadTipo(e.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-card focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    {docTypes.map(t => (
                      <option key={t.slug} value={t.slug}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Arquivo .docx</Label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg px-4 py-6 cursor-pointer text-center transition-colors ${
                      uploadFile ? "border-statusSuccess/30 bg-statusSuccessBg" : "border-border hover:border-border hover:bg-muted"
                    }`}
                  >
                    {uploadFile ? (
                      <div className="flex flex-col items-center gap-1">
                        <FileText className="w-6 h-6 text-emerald-500" />
                        <p className="text-sm font-medium text-statusSuccess truncate max-w-full">{uploadFile.name}</p>
                        <p className="text-xs text-emerald-500">{(uploadFile.size / 1024).toFixed(0)} KB</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-muted-foreground">
                        <Upload className="w-6 h-6" />
                        <p className="text-sm">Clique para selecionar o arquivo .docx</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={e => setUploadFile(e.target.files?.[0] ?? null)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseUploadDialog}>Cancelar</Button>
                <Button onClick={handleUploadTemplate} disabled={uploading || !uploadFile || !uploadNome.trim()}>
                  {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Enviando…</> : "Enviar"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TabButton({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 pb-3 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-blue-500 text-blue-600"
          : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
          active ? "bg-statusInfoBg text-blue-600" : "bg-muted text-muted-foreground"
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}

function DocRow({ doc, tName, tipoLabel, onEdit, onRemove }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-card">
      <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border shrink-0 ${tipoColor(doc.tipo)}`}>
        {tipoLabel(doc.tipo)}
      </span>
      <div className="flex-1 min-w-0">
        {tName
          ? <p className="text-sm text-foreground truncate">{tName}</p>
          : <p className="text-sm text-muted-foreground italic">Sem template vinculado</p>
        }
      </div>
      <EtapaBadge etapa={doc.etapa ?? 1} obrigatoria={doc.obrigatoria ?? true} />
      {doc.reenviar_se_banco_mudar && (
        <span className="text-xs font-medium px-2 py-0.5 rounded-full border bg-statusWarningBg text-statusWarning border-statusWarning/20 shrink-0">
          Reenvio c/ banco
        </span>
      )}
      <RowActions onEdit={onEdit} onRemove={onRemove} />
    </div>
  );
}

function ChildDocRow({ doc, tName, tipoLabel, onEdit, onRemove }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-muted border-t border-border">
      <CornerDownRight className="w-3.5 h-3.5 text-slate-300 shrink-0 ml-1" />
      <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border shrink-0 ${tipoColor(doc.tipo)}`}>
        {tipoLabel(doc.tipo)}
      </span>
      <div className="flex-1 min-w-0">
        {tName
          ? <p className="text-sm text-muted-foreground truncate">{tName}</p>
          : <p className="text-sm text-muted-foreground italic">Sem template vinculado</p>
        }
      </div>
      <EtapaBadge etapa={doc.etapa ?? 1} obrigatoria={doc.obrigatoria ?? true} />
      {doc.reenviar_se_banco_mudar && (
        <span className="text-xs font-medium px-2 py-0.5 rounded-full border bg-statusWarningBg text-statusWarning border-statusWarning/20 shrink-0">
          Reenvio c/ banco
        </span>
      )}
      <RowActions onEdit={onEdit} onRemove={onRemove} />
    </div>
  );
}

function RowActions({ onEdit, onRemove }) {
  return (
    <div className="flex gap-0.5 shrink-0">
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit}>
        <Edit className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onRemove}>
        <Trash className="h-3.5 w-3.5 text-red-400" />
      </Button>
    </div>
  );
}
